// Display-label resolution for the cost-breakdown chart.
// Shared between CostBreakdown (tabs + bar names) and CostChartTooltip
// (per-row labels) so both surfaces render the same casing for the
// same key.

const COST_TYPE_LABEL_OVERRIDES: Record<string, string> = {
  ai_services: "AI Services",
  ai_inference: "AI Inference",
  // Residual of Snowflake's "ai services" billing line that no Cortex usage
  // view itemizes. Composition varies by tenant (legacy functions, Cortex
  // Search serving, reserved capacity); the dollar value reconciles to the
  // AI_SERVICES billing line.
  ai_services_other: "Other AI Services",
  cortex_code_cli: "Cortex Code CLI",
  cortex_code_snowsight: "Cortex Code Snowsight",
  cortex_code_desktop: "Cortex Code Desktop",
  cortex_agent: "Cortex Agents",
  snowflake_intelligence: "Snowflake Intelligence",
  reader_ai_services: "Reader AI Services",
  reader_ai_inference: "Reader AI Inference",
  reader_cortex_code_snowsight: "Reader Cortex Code Snowsight",
  document_ai: "Document AI",
};

// Tokens that should render upper-cased in the title-case fallback.
// Snowflake / cloud terminology that the casing pass would otherwise
// mangle (e.g. "ai_services" → "Ai Services").
const TITLE_CASE_ACRONYMS = new Set([
  "ai",
  "ml",
  "bi",
  "aws",
  "gcp",
  "cli",
  "api",
  "sql",
  "etl",
  "ui",
  "url",
  "qr",
  "ux",
  "gpu",
  "cpu",
  "dbt",
  "rbac",
  "iam",
]);

// Split on both underscore AND space so the function is idempotent —
// applying it twice (e.g. once at field construction, again in the
// tooltip) produces the same result. Splitting on space matters: a
// previously title-cased label like "Trust Center" must come back as
// "Trust Center", not "Trust center".
const toTitleCase = (s: string): string =>
  s
    .split(/[_ ]+/)
    .filter(w => w.length > 0)
    .map(w => {
      const lower = w.toLowerCase();
      if (TITLE_CASE_ACRONYMS.has(lower)) return lower.toUpperCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");

/**
 * Resolve a human-readable label for a cost-type key.
 *
 * Hand-curated overrides win first (strings with acronyms or product
 * names that the title-case fallback can't derive — e.g. "AI Services"
 * not "Ai Services"). Anything else falls through to `toTitleCase`,
 * which lower-cases each token and upper-cases acronyms in
 * TITLE_CASE_ACRONYMS.
 *
 * **Idempotent** for both snake_case and already-title-cased inputs
 * (splits on `[_ ]+` and re-cases each token). Important because the
 * dynamic Other-tab fields pass through this function at construction
 * AND the tooltip applies it again at render — the second call must
 * return the same string.
 */
export const getCostTypeLabel = (label: string): string => {
  if (COST_TYPE_LABEL_OVERRIDES[label]) return COST_TYPE_LABEL_OVERRIDES[label];
  return toTitleCase(label);
};
